import { STATUS_KEYS, STATUS_VERB } from './taskVocab';

/**
 * The status rows of a group, read off the one table that binds them.
 *
 * These were four hand-written entries here and five in `TaskCommands.tsx`, and the fifth is how
 * `#` came to be bound and unlisted — the exact state this file's header says must never hold.
 * `STATUS_KEYS` is now the only place a key is paired with a status, so a sixth transition arrives
 * in the help by existing rather than by somebody remembering this file.
 *
 * `only` is the board's: it binds `x` and the three column keys and **not** `#`, because there is
 * no Dropped column to move a card into. That is a real difference between the two scopes rather
 * than an omission, so it is said as a filter and not as a second list.
 */
const statusRows = (only?: readonly string[]): [string, string][] =>
  STATUS_KEYS.filter(({ status }) => only === undefined || only.includes(status)).map(
    ({ status, key }) => [key, STATUS_VERB[status].sentence],
  );

/**
 * Every key the app binds, in one place.
 *
 * There were two documents. The `?` panel listed the global keys; the Tasks list printed its own
 * legend at the bottom of a 3101px page; the board printed a third 3813px down. Two personas
 * called the panel and the footer "two disjoint documents", and they were right — the footer
 * advertised eight keys the panel did not mention, and the panel has never mentioned the mouse
 * gesture that turns out to be the best thing in the app.
 *
 * One list, read by both. A key that is bound and not listed here is a key nobody will find, and
 * a key listed here and not bound is a lie — so this file is the thing to edit when either
 * changes, and `Shortcuts` and the list's own hint both render from it rather than restating it.
 */

/**
 * Which screen a group of keys applies to. `null` means everywhere.
 *
 * The list and the board are separate scopes because they bind *different keys*, and one group
 * covering both was a document that lied to whichever of them you were on: it listed `x`,
 * `Shift-Enter` and `Ctrl-click`, none of which the board binds, while the board's own hand-written
 * legend advertised `1 2 3`, which the list's did not. `h` and `l` mean different things on each,
 * which one group cannot express at all.
 *
 * A scope here is claimed by the component that binds it, through `useKeys` — see `keyScope.ts`.
 * Adding a group and forgetting the screen, or the other way round, fails
 * `keyScopesAreClaimed.spec.ts`; it does not wait for somebody to press `?` on the screen.
 */
export type KeyScope = null | 'tasks' | 'board' | 'goals' | 'today' | 'task' | 'now';

export interface KeyGroup {
  where: string;
  scope: KeyScope;
  keys: [key: string, what: string][];
  /**
   * The handful worth printing beside the thing they act on, in order.
   *
   * The full group is what `?` shows; this is what fits on one line above a list without becoming
   * the list's largest element.
   */
  inline?: string[];
}

export const KEY_GROUPS: KeyGroup[] = [
  {
    where: 'Anywhere',
    scope: null,
    keys: [
      ['c', 'Capture a task'],
      ['/', 'Search everything'],
      ['?', 'This list'],
      ['Esc', 'Close whatever is open'],
    ],
  },
  {
    where: 'On the task list',
    scope: 'tasks',
    keys: [
      ['j', 'Down a row'],
      ['k', 'Up a row'],
      /*
        The same two keys the goal tree binds, for the same action.

        Four collapsed parents with `›` twisties and nine subtasks between them were mouse-only:
        parking the cursor on one and pressing `l`, `h` or an arrow left every `aria-expanded` at
        `false`. The panel explained why — on this screen `h`/`l` were captioned "Left/Right a
        column (board)", a board meaning printed on a list. The goal tree has bound these two to
        exactly this action since Phase 31.
      */
      ['l', 'Open the subtasks under the cursor'],
      ['h', 'Close them again'],
      ['Space', 'Select, or add to the selection'],
      /*
        The keyboard's answer to shift-click, which it did not have.

        Every path into a batch needed the mouse: `Shift-click` took a span, `Space` took one row
        per press, and `selectAll` was written and bound to nothing at all. Listed beside the
        gesture they mirror, because a reader looking for "how do I take a run of rows" should find
        both answers in one place.
      */
      ['Shift-j', 'Extend the selection down a row'],
      ['Shift-k', 'Extend the selection up a row'],
      ['a', 'Select every row shown'],
      /*
        The undocumented best thing in the app.

        The manager found it by guessing: filter to Overdue, click the first row, shift-click the
        last, and the whole span selects — "25 selected", then one bulk edit. Four minutes of work
        in six clicks. The hint line advertised only `Space`, which is fifty keystrokes for the
        same job.
      */
      ['Shift-click', 'Select everything between two rows'],
      ['Ctrl-click', 'Add one row to the selection'],
      ['Enter', 'Open the task'],
      ['Shift-Enter', 'Open it in the full view'],
      /* The same acts the selection bar offers, from the same table — this column said "Mark it
         done" for the button beside it that said "Done" and the panel that said "Complete". And
         now from the same *table* rather than the same words: the list's handler dispatches on
         `COMMANDS`, so every key in it is bound here, `#` included. */
      ...statusRows(),
    ],
    inline: ['j', 'k', 'Space', 'Shift-click', 'x', 'Enter'],
  },
  {
    /*
      The board's own set, which is not the list's.

      `x`, `Shift-Enter` and `Ctrl-click` are not bound here and were listed; `1`, `2` and `3` are
      bound here and were advertised only by a hand-written legend the panel had never heard of.
      `TaskBoard` reads this now, so the two cannot drift again.

      It is still not the list's set, and three of the gaps have since closed rather than being
      re-documented. Recording a divergence is not justifying one: `x` is the most-used act in the
      app and was missing from half the Tasks screen, `a` left the selection at 1 here while taking
      29 rows there, and Shift-j/k had no board meaning at all. All three were bindings this file
      described as absent with no reason beside them — the machinery was already on
      `useTaskSelection`.

      `h` and `l` stay column moves, which is the one divergence with an argument. On the list they
      open and close the subtasks under the cursor, and a board card has no twisty to open — the
      card draws a bare `0/3` chip and nothing to expand (`boardSubtasks.e2e.spec.ts`) — so there
      is no second meaning here to misfire into. The arrow keys move columns too, for anyone who
      reads `h`/`l` the other way.

      `Shift-Enter` and `Ctrl-click` are still absent and still unexplained; they were not part of
      what this round measured.
    */
    where: 'On the board',
    scope: 'board',
    keys: [
      ['j', 'Down a card'],
      ['k', 'Up a card'],
      ['h', 'Left a column'],
      ['l', 'Right a column'],
      ['Space', 'Select, or add to the selection'],
      ['Shift-j', 'Extend the selection down a card'],
      ['Shift-k', 'Extend the selection up a card'],
      ['a', 'Select every card shown'],
      ['Enter', 'Open the task'],
      /* Not `#`: the board moves a card into a *column*, and there is no Dropped column. */
      ...statusRows(['done', 'inbox', 'next', 'in_progress']),
    ],
    inline: ['h', 'j', 'k', 'l', 'Space', 'x', '1', 'Enter'],
  },
  {
    /*
      The full read view, which had no scope and so inherited the list's.

      `?` on it printed the whole of the list's group — every key the list binds and this screen
      does not — because the list is unmounted while the read view holds the pane, so every one of
      them was a no-op. `Esc` too:
      which `Anywhere` promises closes whatever is open, did not leave the view either. `← Back`
      was the only way out. These two and `Esc` are what `TaskView` binds; `Esc` is not restated
      here, because `Anywhere` already says it and now it is true.

      Named `task` beside the list's `tasks` deliberately: the union type makes a mistyped scope a
      compile error, and a group nobody claims fails the guard.
    */
    where: 'On the open task',
    scope: 'task',
    keys: [
      ['e', 'Edit it in the panel'],
      ['x', STATUS_VERB.done.sentence],
    ],
  },
  {
    where: 'On the goal tree',
    scope: 'goals',
    keys: [
      ['j', 'Down a row'],
      ['k', 'Up a row'],
      ['l', 'Open the goal under the cursor'],
      ['h', 'Close it again'],
      ['Enter', 'Go to the goal'],
    ],
    inline: ['j', 'k', 'l', 'h', 'Enter'],
  },
  {
    where: 'On Today',
    scope: 'today',
    keys: [
      ['[', 'Previous day'],
      [']', 'Next day'],
      ['t', 'Back to today'],
    ],
  },
  {
    /*
      The doing screen, which is four keys and an exit.

      `Enter` and `D` are the two acts the screen exists for and are the same service writes the
      row's status control and the block bar make — `x` on the task list and `Complete` on the
      block bar are this same `completeTask`, so the word is `Complete` here too rather than a
      third spelling of one act. `Esc` is not restated: `Anywhere` already promises it and, on
      this screen, it is true.
    */
    where: 'On Now',
    scope: 'now',
    keys: [
      ['Enter', 'Tick the step you are on'],
      ['D', STATUS_VERB.done.sentence],
      ['+', 'Give the block fifteen more minutes'],
      ['N', 'Move to the next block'],
    ],
    inline: ['Enter', 'D', '+', 'N'],
  },
];

/** The group for a screen, if it has one. */
function keysFor(scope: Exclude<KeyScope, null>): KeyGroup | undefined {
  return KEY_GROUPS.find((g) => g.scope === scope);
}

/** The `inline` subset of a screen's group, as `[key, what]` pairs, in the declared order. */
export function inlineKeys(scope: Exclude<KeyScope, null>): [string, string][] {
  const group = keysFor(scope);
  if (!group?.inline) return [];
  return group.inline
    .map((key) => group.keys.find(([k]) => k === key))
    .filter((pair): pair is [string, string] => pair !== undefined);
}
